/**
 * The packet's page frame and its small parts (Matt's primitives P1 header
 * band, P2 stop badge, P3 footer, P4 temperature tag, P5 diet tag, grey
 * section band).
 *
 * Paper rules, all from the iPhone PDF route (`print-packet-markup.ts`): no
 * `style` attribute, no `<img>`, no `url(` — only Tailwind classes, inline SVG
 * and text survive the server's allowlist. Explicit fills print because every
 * page sets `print-color-adjust: exact` (it inherits).
 */

import { cn } from '@ordo/ui/cn'
import { createContext, type ReactNode, useContext } from 'react'

import { OrdoWordmark, PrintHeaderNotice } from '@/components/print/print-header'
import { titleCase } from '@/lib/chefs/packet/title-case'
import type {
  BandSpace,
  DateBox as DateBoxData,
  PacketBand,
  PacketTitle,
  RouteCode,
  StopType,
} from '@/lib/chefs/packet/units'
import { qrPath } from '@/lib/chefs/route-qr'
import type { Temperature } from '@/lib/chefs/types'

/**
 * The packet prints on letter with quarter-inch margins (Abner, Oct 18:
 * "remove the white margins of the page as much as possible"): the
 * `ordo-orders-packet` page in `globals.css`, and `printPacketPdfOptions` on
 * the server for the iPhone PDF. 8.5in less two 0.25in margins at 96px/in.
 */
export const PACKET_CONTENT_WIDTH_PX = 768

/**
 * The page box: the 1008px letter content area less 4px, so rounding can
 * never push a sheet onto a second page. A page that still runs over grows
 * (min height), and the browser carries the rest to the next sheet: never a
 * clip.
 */
export const PACKET_PAGE_HEIGHT_PX = 1004

/** The class that puts a page on the packet's named print page (`globals.css`). */
export const ORDERS_PACKET_PAGE_CLASS = 'print-page-orders-packet'

/** What a page needs from the laid-out packet; placeholders while measuring. */
export interface PacketRenderContextValue {
  /** "4–6" for a set of sections; null when none printed. */
  pageOf: (sectionIds: readonly string[]) => string | null
  totalPages: number | null
}

export const PacketRenderContext = createContext<PacketRenderContextValue>({
  pageOf: () => '00–00',
  totalPages: null,
})

export function usePacketRender(): PacketRenderContextValue {
  return useContext(PacketRenderContext)
}

/**
 * The band's counter: the sheet's place among its kind when the model set one ("1 OF 23"), else the page's place in
 * a section that runs past a page ("2 OF 3"), else none. The header no longer prints it (the Oct 21 canvas); a sheet
 * the band counts by kind says it in its eyebrow instead.
 */
export function bandCounter(band: Pick<PacketBand, 'counter'>, index: number, count: number): string | null {
  return band.counter ?? (count > 1 ? `${index + 1} OF ${count}` : null)
}

/**
 * The two line weights of the Oct 21 canvas, the only two the packet draws: `RULE` (1pt) under a section's title and
 * around an outlined box or a checkbox, `DIVIDER` (0.5pt) between table rows. A column head has no rule under it.
 */
export const RULE = 'border-[#1a1a1a] border-[1pt]'
export const RULE_BOTTOM = 'border-b-[1pt] border-[#1a1a1a]'
export const DIVIDER = 'border-b-[0.5pt] border-[#bbbbbb]'

/** A band's title over its rule: "Lunch", "Cook · Creamy Cheddar Mac", "Sign-Off". */
export const BAND_TITLE = 'text-[16px] font-extrabold leading-[1.25]'

/**
 * The header (the Oct 21 canvas): the Ordo mark on the left; on the right the copy's label, "KITCHEN COPY" on the
 * kitchen's pages, the caterer on a kitchen stop sheet, and a solid black "SCHOOL COPY" tag on the school's (`rightTag`),
 * beside the INCOMPLETE ORDER label when it applies. Nothing else: the page's title says what it is.
 */
function Band({ band }: { band: PacketBand }) {
  return (
    <div
      data-packet-band={band.style}
      className="flex min-h-[34px] shrink-0 items-end justify-between gap-4 border-b-[0.5pt] border-[#1a1a1a] pb-2 text-black">
      <OrdoWordmark className="h-[18px] w-auto" />
      <span className="flex max-w-[420px] flex-wrap items-center justify-end gap-2 text-right">
        <PrintHeaderNotice className="rounded-sm bg-white px-0.5 py-0.5 normal-case tracking-normal" />
        {band.right ? (
          <span
            data-packet-band-right={band.rightTag ? 'tag' : 'label'}
            className={
              band.rightTag
                ? 'rounded-[5px] bg-[#1a1a1a] px-2.5 py-1 text-[13px] font-extrabold leading-[1.2] tracking-[1px] text-white'
                : 'text-[12px] font-bold tracking-[0.6px] text-[#555555]'
            }>
            {band.right}
          </span>
        ) : null}
      </span>
    </div>
  )
}

/**
 * The date stamp, the one place a page says its date (Hunter, Oct 20: "it should always be in the date stamp. It
 * shouldn't be different from page to page"): "THU" over "OCT 15" in an outlined box, the same size on every page.
 */
export function DateBox({ box }: { box: DateBoxData }) {
  return (
    <span
      data-packet-date-box=""
      className={cn(
        'flex h-[72px] w-[92px] shrink-0 flex-col items-center justify-center rounded-lg text-center leading-none',
        RULE,
      )}>
      <span className="text-[12px] font-bold tracking-[1px]">{box.weekday}</span>
      <span
        className={cn('mt-1 whitespace-nowrap font-extrabold', box.date.length > 6 ? 'text-[15px]' : 'text-[22px]')}>
        {box.date}
      </span>
    </span>
  )
}

/**
 * The route's one code, top right of the day cover (the Oct 21 canvas): the route page, every stop by name, tap one to
 * navigate; or, without a signed link, the whole route in Google Maps.
 */
export function RouteCodeStamp({ code }: { code: RouteCode }) {
  const page = code.opens === 'route-page'
  return (
    <span data-packet-route-code={code.opens} className="ml-auto flex shrink-0 flex-col items-center gap-1">
      <PacketQr
        url={code.url}
        label={page ? 'QR code for the delivery route page' : 'QR code for the route in Google Maps'}
        size={84}
      />
      <span className="text-[10px] font-bold tracking-[0.8px] text-[#555555]">
        {page ? 'SCAN FOR ROUTE' : 'SCAN FOR MAPS'}
      </span>
    </span>
  )
}

/**
 * The page title, under the header (the Oct 21 canvas): the date stamp, then the eyebrow over the title in title
 * case and the line under it; on the right a note and the route's code. A sheet the band counts by kind ("RECIPE 3
 * OF 23") counts its own pages in the eyebrow ("· 2 OF 2").
 */
function Title({
  title,
  index,
  count,
  countedByKind,
}: {
  title: PacketTitle
  index: number
  count: number
  countedByKind: boolean
}) {
  return (
    <div className={cn('flex shrink-0 items-center gap-[18px] pb-1 pt-3', title.rule && cn('pb-3', RULE_BOTTOM))}>
      {title.dateBox ? <DateBox box={title.dateBox} /> : null}
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        {title.eyebrow ? (
          <p className="text-[12px] font-bold uppercase tracking-[1px] text-[#555555]">
            {title.eyebrow}
            {countedByKind && count > 1 ? ` · ${index + 1} OF ${count}` : ''}
          </p>
        ) : null}
        <h1 className="text-[30px] font-extrabold leading-[1.1] tracking-[-0.3px]">{titleCase(title.title)}</h1>
        {title.subtitle ? <p className="text-[13px] leading-snug">{title.subtitle}</p> : null}
      </div>
      {title.note ? <p className="max-w-[230px] shrink-0 text-right text-[12px] leading-snug">{title.note}</p> : null}
      {title.code ? <RouteCodeStamp code={title.code} /> : null}
    </div>
  )
}

/** P3: the section's name on the left, the page and when it printed on the right: "Page 4 of 40 · Printed …". */
function Footer({ label, pageLabel, printedAt }: { label: string; pageLabel: string; printedAt: string }) {
  return (
    <div className="mt-auto flex shrink-0 items-center justify-between gap-4 border-t-[0.5pt] border-[#bbbbbb] pb-0.5 pt-1.5 text-[11px] text-[#555555]">
      <span data-packet-footer-label="">{titleCase(label)}</span>
      <span>
        <span data-packet-page-label="">{pageLabel.split(' · ')[0]}</span> · Printed {printedAt}
      </span>
    </div>
  )
}

export interface PacketPageProps {
  band: PacketBand
  title: PacketTitle | null
  /** 0-based page within its section and the section's page count. */
  index: number
  count: number
  pageLabel: string
  /** The footer's left side: the section's name ("Prep Recipes", "Stop Sheets"). */
  footerLabel: string
  printedAt: string
  /**
   * `print`: a page of the packet. `probe`: a fixed page with an empty body
   * the measure host reads the body height off. `stream`: no height, every
   * unit in a row, for measuring each unit.
   */
  mode: 'print' | 'probe' | 'stream'
  /** `data-packet-body` on the probe's body. */
  bodyId?: string
  pageBreak?: boolean
  /** Laid over the whole page box: the INCOMPLETE ORDER watermark of a page whose counts can still change. */
  mark?: ReactNode
  children?: ReactNode
}

export function PacketPage({
  band,
  title,
  index,
  count,
  pageLabel,
  footerLabel,
  printedAt,
  mode,
  bodyId,
  pageBreak = false,
  mark,
  children,
}: PacketPageProps) {
  return (
    <section
      data-packet-page=""
      className={cn(
        'relative flex w-full flex-col bg-white font-sans text-[13px] leading-snug text-[#1a1a1a] [-webkit-print-color-adjust:exact] [print-color-adjust:exact]',
        mode === 'print' && 'min-h-[1004px]',
        mode === 'probe' && 'h-[1004px]',
        pageBreak && 'print-page-break',
      )}>
      {mark}
      <Band band={band} />
      {title ? <Title title={title} index={index} count={count} countedByKind={band.counter !== null} /> : null}
      <div data-packet-body={bodyId} className="flex min-h-0 flex-1 flex-col">
        {children}
      </div>
      <Footer label={footerLabel} pageLabel={pageLabel} printedAt={printedAt} />
    </section>
  )
}

const STOP_TILE_SIZE = {
  sm: 'h-[18px] w-[18px] rounded-[4px] text-[11px]',
  md: 'h-[24px] w-[24px] rounded-[5px] text-[13px]',
  lg: 'h-[34px] w-[34px] rounded-lg text-[18px]',
  /** The stop sheet's badge, the date stamp's height (the Oct 21 canvas). */
  xl: 'h-[72px] w-[72px] rounded-lg text-[40px]',
} as const

/** P2: a stop number, white on black (the Oct 21 canvas). */
export function StopTile({ number, size = 'md' }: { number: string; size?: keyof typeof STOP_TILE_SIZE }) {
  return (
    <span
      data-packet-stop-tile={size}
      className={cn(
        'inline-flex shrink-0 items-center justify-center bg-[#1a1a1a] px-0.5 font-extrabold leading-none text-white',
        STOP_TILE_SIZE[size],
      )}>
      {number}
    </span>
  )
}

const TAG =
  'inline-block whitespace-nowrap rounded-[5px] px-[7px] py-[2px] text-[11px] font-bold leading-[1.3] tracking-[0.6px]'

/**
 * P4 (the Oct 21 canvas): HOT solid black, CHILLED outlined, ROOM grey with no line. ROOM has no box, so it drops the
 * box's inner padding and starts at the column's left edge like every other value (Sheila, Oct 24).
 */
const TEMP_TAG: Record<Temperature, string> = {
  hot: 'border-[1pt] border-[#1a1a1a] bg-[#1a1a1a] text-white',
  chilled: 'border-[1pt] border-[#1a1a1a] bg-white text-[#1a1a1a]',
  ambient: 'border-[1pt] border-transparent bg-white px-0 text-[#555555]',
}

const TEMP_WORD: Record<Temperature, string> = { hot: 'HOT', chilled: 'CHILLED', ambient: 'ROOM' }

export function TempTag({ temperature }: { temperature: Temperature | null }) {
  if (!temperature) {
    return null
  }
  return <span className={cn(TAG, TEMP_TAG[temperature])}>{TEMP_WORD[temperature]}</span>
}

/** P5: outlined diet tags ("HALAL", "VEGETARIAN"), right after the name. */
export function DietTags({ diets }: { diets: readonly string[] }) {
  if (diets.length === 0) {
    return null
  }
  return (
    <>
      {diets.map((diet) => (
        <span key={diet} className={cn(TAG, 'ml-1 border-[1pt] border-[#1a1a1a] align-[1px] text-[9.5px]')}>
          {diet}
        </span>
      ))}
    </>
  )
}

/** A row's flags in words ("Field trip", "K-8 portion"), outlined. */
export function RowTags({ tags }: { tags: readonly string[] }) {
  if (tags.length === 0) {
    return null
  }
  return (
    <>
      {tags.map((tag) => (
        <span
          key={tag}
          className={cn(
            TAG,
            'ml-1 border-[1pt] border-[#1a1a1a] align-[1px] text-[9.5px] font-semibold tracking-normal',
          )}>
          {tag}
        </span>
      ))}
    </>
  )
}

/** P15: BULK / UNITIZED / MIXED. */
export function StopTypeTag({ type }: { type: StopType | null }) {
  if (!type) {
    return null
  }
  return <span className={cn(TAG, 'border-[1pt] border-[#1a1a1a] text-[10px]')}>{type}</span>
}

const TICK_BOX_SIZE = { sm: 'h-[14px] w-[14px]', md: 'h-[16px] w-[16px]', lg: 'h-[20px] w-[20px]' } as const

/** An empty box to tick, at the canvas's 1pt. */
export function TickBox({ size = 'md' }: { size?: keyof typeof TICK_BOX_SIZE }) {
  return <span aria-hidden className={cn('inline-block shrink-0 rounded-[3px] bg-white', RULE, TICK_BOX_SIZE[size])} />
}

/**
 * The packet's spacing scale (Sheila, Oct 24: consistent spacing, as few sheets as legibility allows). Above a band:
 * 10px between the tables of a section, 12px between its groups, 16px between blocks. Every table row pads `ROW_PAD`:
 * 4px, or 2px on a section tightening to keep to one page (`preferOnePage`).
 */
export const BAND_SPACE: Record<BandSpace | 'none', string> = {
  none: 'pt-2.5',
  group: 'pt-3',
  block: 'pt-4',
}

/** A table row's padding: 4px, or 2px when its section prints compact. */
export function rowPad(compact = false): string {
  return compact ? 'py-0.5' : 'py-1'
}

/** "LUNCH · SERVE TODAY" in sentence case: "Lunch · Serve today", "PM snack", "Serve Mon 10/19". */
export function sentenceLabel(text: string): string {
  if (text !== text.toUpperCase()) {
    return text
  }
  return text
    .toLowerCase()
    .replace(/(^|·\s*)([a-z])/g, (_, prefix: string, letter: string) => prefix + letter.toUpperCase())
    .replace(/\b(am|pm|haccp)\b/gi, (word) => word.toUpperCase())
    .replace(
      /\b(mon(?:day)?|tue(?:sday)?|wed(?:nesday)?|thu(?:rsday)?|fri(?:day)?|sat(?:urday)?|sun(?:day)?)\b/gi,
      (word) => word.charAt(0).toUpperCase() + word.slice(1),
    )
}

/** "LUNCH · SERVE TODAY" → "Lunch" over its rule with "Serve today" on the right (the Oct 21 canvas). */
export function bandParts(text: string, note: string | null): { title: string; note: string | null } {
  const match = /^(.*?)\s*·\s*(SERVE\b.*)$/i.exec(text)
  if (match && !note) {
    return { title: sentenceLabel(match[1]!), note: sentenceLabel(match[2]!) }
  }
  return { title: sentenceLabel(text), note: note ? sentenceLabel(note) : null }
}

/**
 * A section's title over its 1pt rule (the Oct 21 canvas: "Lunch", "Serve today" on the right), or a thinner
 * sub-band at level 2. `dark`: a later day's food on a stop summary, its note ("DO NOT SERVE TODAY") in capitals.
 */
export function SectionBand({
  text,
  note,
  level,
  continued: _continued,
  extra,
  space,
  tone,
}: {
  text: string
  note: string | null
  level: 1 | 2
  continued: boolean
  extra?: ReactNode
  space?: BandSpace
  tone?: 'dark'
}) {
  if (level === 2) {
    return (
      <div className="pt-2">
        <p className={cn('pb-0.5 text-[12px] font-bold', DIVIDER)}>
          {sentenceLabel(text)}
          {note ? <span className="ml-2 font-medium">{note}</span> : null}
        </p>
      </div>
    )
  }
  const parts = bandParts(text, note)
  const dark = tone === 'dark'
  return (
    <div className={BAND_SPACE[space ?? 'none']}>
      <div className={cn('flex items-baseline justify-between gap-3 pb-1', RULE_BOTTOM)}>
        <span className={BAND_TITLE}>{parts.title}</span>
        {parts.note || extra ? (
          <span
            className={cn('text-[13px] font-semibold text-[#555555]', dark && 'font-bold uppercase text-[#1a1a1a]')}>
            {parts.note}
            {extra}
          </span>
        ) : null}
      </div>
    </div>
  )
}

/** A QR code as one SVG path, white quiet zone included, built synchronously so it is on the page when printed. */
export function PacketQr({ url, label, size }: { url: string; label: string; size: number }) {
  const qr = qrPath(url)
  if (!qr) {
    return null
  }
  const box = qr.size + 4
  return (
    <svg
      role="img"
      aria-label={label}
      viewBox={`-2 -2 ${box} ${box}`}
      width={size}
      height={size}
      shapeRendering="crispEdges"
      className="shrink-0">
      <rect x={-2} y={-2} width={box} height={box} fill="#ffffff" />
      <path d={qr.d} fill="#1a1a1a" />
    </svg>
  )
}

/** A labelled line to write on: "Packed by ____". */
export function WriteIn({ label, className }: { label: string; className?: string }) {
  return (
    <span className={cn('flex min-w-0 items-end gap-1.5', className)}>
      <span className="whitespace-nowrap">{label}</span>
      <span aria-hidden className="h-4 min-w-6 flex-1 border-b-[0.5pt] border-[#1a1a1a]" />
    </span>
  )
}
